import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';
import {
  Activity,
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Copy,
  Globe2,
  KeyRound,
  RefreshCw,
} from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useExternalPlanSelectionSettings,
  useRotateExternalPlanSelectionSecret,
  useUpdateExternalPlanSelectionSettings,
} from '@/hooks/provider/useExternalPlanSelectionSettings';
import {
  ExternalPlanSelectionRequestError,
  normalizeExternalPartnerDomain,
  normalizeExternalPlanAllowedDomains,
} from '@/services/provider/external-plan-selection.service';
import type { ExternalPlanSelectionMetrics } from '@/types/external-plan-selection';

type FormValues = {
  partnerName: string;
  enabled: boolean;
  allowedDomainsText: string;
};

type FormErrors = {
  partnerName?: string;
  allowedDomains?: string;
  enabled?: string;
};

function formatTimestamp(value: string | null | undefined) {
  if (!value) {
    return '—';
  }

  try {
    const date = new Date(value);
    const relative = formatDistanceToNow(date, { addSuffix: true });
    return `${date.toLocaleString()} (${relative})`;
  } catch (error) {
    console.warn('Failed to format timestamp', value, error);
    return value;
  }
}

function parseAllowedDomains(input: string) {
  const entries = input
    .split(/[\n,]/)
    .map((value) => value.trim())
    .filter(Boolean);

  const domains: string[] = [];
  const invalid: string[] = [];

  entries.forEach((entry) => {
    const host = normalizeExternalPartnerDomain(entry);
    if (host) {
      if (!domains.includes(host)) {
        domains.push(host);
      }
    } else {
      invalid.push(entry);
    }
  });

  return { domains, invalid };
}

function formatMetrics(metrics: ExternalPlanSelectionMetrics | null | undefined) {
  if (!metrics) {
    return null;
  }

  return [
    { key: 'total', label: 'totalIntents', value: metrics.totalIntents, fallback: 'Total intents' },
    {
      key: 'fulfilledWithoutPayment',
      label: 'fulfilledWithoutPayment',
      value: metrics.fulfilledWithoutPayment,
      fallback: 'Fulfilled without payment',
    },
    { key: 'pendingPayment', label: 'pendingPayment', value: metrics.pendingPayment, fallback: 'Pending payment' },
    { key: 'completed', label: 'completed', value: metrics.completed, fallback: 'Completed' },
  ];
}

export function ProviderExternalPlanSelectionCard() {
  const { t } = useTranslation();
  const settingsQuery = useExternalPlanSelectionSettings();
  const updateMutation = useUpdateExternalPlanSelectionSettings();
  const rotateMutation = useRotateExternalPlanSelectionSecret();

  const [formValues, setFormValues] = useState<FormValues>({
    partnerName: '',
    enabled: false,
    allowedDomainsText: '',
  });
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [rotatedSecret, setRotatedSecret] = useState<string | null>(null);
  const [rotationInstructions, setRotationInstructions] = useState<string | undefined>(undefined);

  useEffect(() => {
    const config = settingsQuery.data?.config;
    if (!config) {
      return;
    }

    setFormValues({
      partnerName: config.partnerName,
      enabled: config.enabled,
      allowedDomainsText: config.allowedDomains.join('\n'),
    });
  }, [settingsQuery.data]);

  const isSaving = updateMutation.isPending;
  const isRotating = rotateMutation.isPending;

  const statusIssues = useMemo(() => {
    const issues: Array<'disabled' | 'missingSecret'> = [];
    const config = settingsQuery.data?.config;

    if (!config) {
      return issues;
    }
    if (!config.enabled) {
      issues.push('disabled');
    }
    if (!config.secretPreview) {
      issues.push('missingSecret');
    }

    return issues;
  }, [settingsQuery.data?.config?.enabled, settingsQuery.data?.config?.secretPreview]);

  const metrics = settingsQuery.data?.metrics ?? null;
  const formattedMetrics = useMemo(() => formatMetrics(metrics), [metrics]);

  const handleRetry = () => {
    settingsQuery.refetch();
  };

  const handleSave = async (event: React.FormEvent) => {
    event.preventDefault();
    const trimmedName = formValues.partnerName.trim();
    const { domains, invalid } = parseAllowedDomains(formValues.allowedDomainsText);

    const nextErrors: FormErrors = {};
    if (!trimmedName) {
      nextErrors.partnerName = t(
        'settings.externalPlanSelection.validation.partnerName',
        'Partner name is required.',
      );
    }

    if (invalid.length > 0) {
      nextErrors.allowedDomains = t(
        'settings.externalPlanSelection.validation.allowedDomains',
        'Enter valid hostnames without protocols or paths.',
      );
    }

    setFormErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    const normalizedDomains = normalizeExternalPlanAllowedDomains(domains);

    try {
      const updated = await updateMutation.mutateAsync({
        partnerName: trimmedName,
        enabled: formValues.enabled,
        allowedDomains: normalizedDomains,
      });

      setFormValues((previous) => ({
        ...previous,
        partnerName: updated.partnerName,
        enabled: updated.enabled,
        allowedDomainsText: updated.allowedDomains.join('\n'),
      }));
      toast.success(
        t(
          'settings.externalPlanSelection.saveSuccess',
          'External partner settings saved.',
        ),
      );
    } catch (error) {
      if (error instanceof ExternalPlanSelectionRequestError) {
        const apiErrors = error.errors ?? {};
        setFormErrors((previous) => ({
          ...previous,
          partnerName: apiErrors.partner_name?.[0] ?? previous.partnerName,
          allowedDomains: apiErrors.allowed_domains?.[0] ?? previous.allowedDomains,
          enabled: apiErrors.enabled?.[0] ?? previous.enabled,
        }));
      }

      const message =
        error instanceof Error && error.message
          ? error.message
          : t(
              'settings.externalPlanSelection.saveError',
              'Unable to update partner settings. Try again.',
            );
      toast.error(message);
    }
  };

  const handleRotate = async () => {
    try {
      const result = await rotateMutation.mutateAsync();
      setRotatedSecret(result.secret);
      setRotationInstructions(result.instructions);
      toast.success(
        t('settings.externalPlanSelection.rotateSuccess', 'Secret rotated. Copy it now.'),
      );
    } catch (error) {
      const message =
        error instanceof Error && error.message
          ? error.message
          : t(
              'settings.externalPlanSelection.rotateError',
              'Unable to rotate the partner secret.',
            );
      toast.error(message);
    }
  };

  const copySecret = async () => {
    if (rotatedSecret === null) {
      return;
    }

    try {
      await navigator.clipboard.writeText(rotatedSecret);
      toast.success(t('settings.externalPlanSelection.copySuccess', 'Secret copied.'));
    } catch (error) {
      console.error('Copy secret failed', error);
      toast.error(
        t('settings.externalPlanSelection.copyError', 'Unable to copy the secret to clipboard.'),
      );
    }
  };

  const normalizeDomainsPreview = () => {
    const { domains } = parseAllowedDomains(formValues.allowedDomainsText);
    const normalizedDomains = normalizeExternalPlanAllowedDomains(domains);
    setFormValues((previous) => ({
      ...previous,
      allowedDomainsText: normalizedDomains.join('\n'),
    }));
  };

  const renderStatus = () => {
    if (!settingsQuery.data?.config) {
      return null;
    }

    const Icon = statusIssues.length > 0 ? AlertTriangle : CheckCircle2;
    const variant = statusIssues.length > 0 ? 'destructive' : 'default';

    return (
      <Alert variant={variant}>
        <Icon className="h-4 w-4" />
        <div>
          <AlertTitle>
            {statusIssues.length > 0
              ? t(
                  'settings.externalPlanSelection.status.actionNeeded',
                  'Partner endpoint is not ready.',
                )
              : t('settings.externalPlanSelection.status.ready', 'Integration is ready')}
          </AlertTitle>
          <AlertDescription className="space-y-1 text-sm">
            {statusIssues.includes('disabled') && (
              <p>
                {t(
                  'settings.externalPlanSelection.status.disabled',
                  'Integration is disabled. Enable it to accept partner calls.',
                )}
              </p>
            )}
            {statusIssues.includes('missingSecret') && (
              <p>
                {t(
                  'settings.externalPlanSelection.status.missingSecret',
                  'No active shared secret found. Rotate to issue a new secret.',
                )}
              </p>
            )}
            {statusIssues.length === 0 && (
              <p>
                {t(
                  'settings.externalPlanSelection.status.healthy',
                  'Partner domain and secret are configured. Incoming partner requests will be accepted.',
                )}
              </p>
            )}
          </AlertDescription>
        </div>
      </Alert>
    );
  };

  const renderMetrics = () => {
    if (!formattedMetrics) {
      return null;
    }

    return (
      <div className="rounded-lg border p-4">
        <div className="mb-3 flex items-center gap-2">
          <Activity className="h-4 w-4" />
          <p className="text-sm font-medium">
            {t(
              'settings.externalPlanSelection.metrics.title',
              'External plan selection usage',
            )}
          </p>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {formattedMetrics.map((metric) => (
            <div
              key={metric.key}
              className="rounded-lg border bg-muted/30 p-3"
            >
              <p className="text-xs text-muted-foreground">
                {t(
                  `settings.externalPlanSelection.metrics.${metric.label}`,
                  metric.fallback,
                )}
              </p>
              <p className="text-2xl font-semibold">{metric.value}</p>
            </div>
          ))}
          <div className="rounded-lg border bg-muted/30 p-3">
            <p className="text-xs text-muted-foreground">
              {t('settings.externalPlanSelection.metrics.lastIntentAt', 'Last intent at')}
            </p>
            <p className="text-sm font-medium">{formatTimestamp(metrics?.lastIntentAt)}</p>
          </div>
        </div>
      </div>
    );
  };

  if (settingsQuery.isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe2 className="h-5 w-5" />
            {t('settings.externalPlanSelection.title', 'External partner plan selection')}
          </CardTitle>
          <CardDescription>
            {t(
              'settings.externalPlanSelection.description',
              'Configure allowed partner domains and shared secret used for external plan selection requests.',
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-10 w-40" />
        </CardContent>
      </Card>
    );
  }

  if (settingsQuery.isError) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe2 className="h-5 w-5" />
            {t('settings.externalPlanSelection.title', 'External partner plan selection')}
          </CardTitle>
          <CardDescription>
            {t(
              'settings.externalPlanSelection.description',
              'Configure allowed partner domains and shared secret used for external plan selection requests.',
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="flex items-center justify-between gap-4">
              <span>
                {t(
                  'settings.externalPlanSelection.loadError',
                  'Unable to load partner settings. Check your session or try again later.',
                )}
              </span>
              <Button variant="secondary" size="sm" onClick={handleRetry} className="gap-2">
                <RefreshCw className="h-4 w-4" />
                {t('common.retry', 'Retry')}
              </Button>
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <CardTitle className="flex items-center gap-2">
            <Globe2 className="h-5 w-5" />
            {t('settings.externalPlanSelection.title', 'External partner plan selection')}
          </CardTitle>
          {settingsQuery.data?.config.enabled ? (
            <Badge variant="secondary">
              {t('settings.externalPlanSelection.badges.enabled', 'Enabled')}
            </Badge>
          ) : (
            <Badge variant="destructive">
              {t('settings.externalPlanSelection.badges.disabled', 'Disabled')}
            </Badge>
          )}
        </div>
        <CardDescription>
          {t(
            'settings.externalPlanSelection.description',
            'Configure allowed partner domains and shared secret used for external plan selection requests.',
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {renderStatus()}

        {rotatedSecret !== null ? (
          <Alert>
            <KeyRound className="h-4 w-4" />
            <div className="space-y-2">
              <AlertTitle>
                {t(
                  'settings.externalPlanSelection.secretOneTime',
                  'Copy the new shared secret now',
                )}
              </AlertTitle>
              <AlertDescription className="space-y-2">
                <div className="flex items-center gap-3">
                  <code className="relative block flex-1 truncate rounded-md bg-muted px-3 py-2 text-sm">
                    {rotatedSecret}
                  </code>
                  <Button size="sm" variant="outline" onClick={copySecret} className="gap-2">
                    <Copy className="h-4 w-4" />
                    {t('settings.externalPlanSelection.copySecret', 'Copy secret')}
                  </Button>
                </div>
                {rotationInstructions ? (
                  <p className="text-xs text-muted-foreground">{rotationInstructions}</p>
                ) : null}
              </AlertDescription>
            </div>
          </Alert>
        ) : null}

        <form className="space-y-6" onSubmit={handleSave}>
          <div className="grid gap-4 lg:grid-cols-3">
            <div className="space-y-2 lg:col-span-2">
              <Label htmlFor="partner-name">
                {t('settings.externalPlanSelection.fields.partnerName', 'Partner name')}
              </Label>
              <Input
                id="partner-name"
                value={formValues.partnerName}
                onChange={(event) =>
                  setFormValues((previous) => ({ ...previous, partnerName: event.target.value }))
                }
                placeholder={t(
                  'settings.externalPlanSelection.placeholders.partnerName',
                  'Acme Partner',
                )}
              />
              {formErrors.partnerName ? (
                <p className="text-sm text-destructive">{formErrors.partnerName}</p>
              ) : null}
            </div>
            <div className="flex items-center justify-between rounded-lg border p-4">
              <div>
                <p className="font-medium">
                  {t(
                    'settings.externalPlanSelection.fields.enabled',
                    'Enable partner integration',
                  )}
                </p>
                <p className="text-sm text-muted-foreground">
                  {t(
                    'settings.externalPlanSelection.helpers.enabled',
                    'When disabled, partner requests will be rejected.',
                  )}
                </p>
                {formErrors.enabled ? (
                  <p className="text-sm text-destructive">{formErrors.enabled}</p>
                ) : null}
              </div>
              <Switch
                checked={formValues.enabled}
                onCheckedChange={(checked) =>
                  setFormValues((previous) => ({ ...previous, enabled: checked }))
                }
              />
            </div>
          </div>

          <div className="grid gap-4 lg:grid-cols-3">
            <div className="space-y-2 lg:col-span-2">
              <Label htmlFor="allowed-domains">
                {t('settings.externalPlanSelection.fields.allowedDomains', 'Allowed domains')}
              </Label>
              <Textarea
                id="allowed-domains"
                value={formValues.allowedDomainsText}
                onChange={(event) =>
                  setFormValues((previous) => ({ ...previous, allowedDomainsText: event.target.value }))
                }
                onBlur={normalizeDomainsPreview}
                placeholder={t(
                  'settings.externalPlanSelection.placeholders.allowedDomains',
                  'partner.example.com\napi.partner.example.com',
                )}
                rows={4}
              />
              <p className="text-xs text-muted-foreground">
                {t(
                  'settings.externalPlanSelection.helpers.allowedDomains',
                  'One hostname per line. Protocols and paths are stripped automatically.',
                )}
              </p>
              {formErrors.allowedDomains ? (
                <p className="text-sm text-destructive">{formErrors.allowedDomains}</p>
              ) : null}
            </div>
            <div className="space-y-3 rounded-lg border p-4">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <p className="font-medium">
                    {t('settings.externalPlanSelection.fields.secretPreview', 'Secret preview')}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {t(
                      'settings.externalPlanSelection.helpers.secretPreview',
                      'Rotate to view the full shared secret.',
                    )}
                  </p>
                </div>
                <Badge variant="outline" className="gap-1">
                  <KeyRound className="h-3 w-3" />
                  {t('settings.externalPlanSelection.badges.secret', 'Shared secret')}
                </Badge>
              </div>
              <Input
                readOnly
                value={settingsQuery.data?.config.secretPreview ?? '—'}
                className="bg-muted"
              />
              <div className="text-xs text-muted-foreground">
                <p>
                  {t('settings.externalPlanSelection.fields.lastRotatedAt', 'Last rotated')}: {formatTimestamp(settingsQuery.data?.config.lastRotatedAt)}
                </p>
              </div>
              <Button
                type="button"
                onClick={handleRotate}
                variant="secondary"
                disabled={isRotating}
                className="gap-2"
              >
                {isRotating && <RefreshCw className="h-4 w-4 animate-spin" />}
                {!isRotating && <KeyRound className="h-4 w-4" />}
                {t('settings.externalPlanSelection.actions.rotate', 'Rotate secret')}
              </Button>
              <p className="text-xs text-muted-foreground">
                {t(
                  'settings.externalPlanSelection.helpers.rotate',
                  'You will receive the full secret immediately after rotation.',
                )}
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <Button type="submit" disabled={isSaving} className="gap-2">
              {isSaving && <RefreshCw className="h-4 w-4 animate-spin" />}
              {t('settings.externalPlanSelection.actions.save', 'Save changes')}
            </Button>
          </div>
        </form>

        {renderMetrics()}
      </CardContent>
    </Card>
  );
}
